import { useState, useEffect, useRef } from "react";
import { NavLink, Outlet, Link, Navigate } from "react-router-dom";
import { useNavigate } from "@/lib/useNav";
import {
  LayoutDashboard, Building2, Users, TrendingUp,
  CheckSquare, ListTodo, ListChecks, Target, BarChart3, Settings, Printer, Megaphone, Mail, Hotel, Ticket,
  Search, LogOut, Menu, X, Sparkles, MessageCircle, Bell, Handshake, KeyRound, BookOpen, Percent, Wine, CalendarRange, Pin, LineChart, Gauge, Pencil, Check } from "lucide-react";
import { NavEditor, applyNavLayout } from "@/components/layout/NavEditor";
import { InstallBanner } from "@/components/layout/InstallApp";
import { QuickAddFab } from "@/components/layout/QuickAddFab";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { cn } from "@/lib/utils";
import { Avatar } from "@/components/shared/UI";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { PasskeyPrompt } from "@/components/shared/PasskeyPrompt";
import { TabAlerts } from "@/components/layout/TabAlerts";
import { ChatButton } from "@/components/chat/ChatButton";
import { toast } from "sonner";
import { usePermissions } from "@/context/PermissionsContext";
import { warmOptions } from "@/components/hooks/useOptions";
import { fetchPropertyGroups } from "@/components/form/PropertyGroupMenu";
import { SystemStatus } from "@/components/layout/SystemStatus";
import { ChangeWatcher } from "@/lib/changes";

export const PATH_PAGE = {
  "/dashboard": "dashboard", "/properties": "properties", "/organisations": "organisations",
  "/contacts": "contacts", "/opportunities": "opportunities", "/pipeline": "pipeline",
  "/todo": "todo", "/actions/my": "actions", "/actions/all": "actions",
  "/print-orders": "print_orders", "/rates": "actions", "/campaigns": "campaigns", "/campaigns/direct": "campaigns", "/campaigns/agent": "campaigns", "/kpis": "kpis",
  "/reports": "reports", "/stats": "stats", "/forecasts": "forecasts", "/users": "users", "/settings": "settings",
  "/configuration": "configuration", "/roles": "roles", "/audit-log": "users",
  "/collaborations": "collaborations", "/alcohol-orders": "alcohol_orders", "/prize-vouchers": "prize_vouchers",
  "/logins": "logins", "/contracted-rates": "rate_contracts", "/noticeboard": "noticeboard",
  "/event-calendar": "events", "/procedures": "procedures",
};

// Route-level guard so direct URL navigation to a page the viewer's role can't see redirects
// instead of mounting a page that just fills up with 403 toasts (the sidebar already hides the
// nav link, but that alone doesn't stop someone typing the URL or following an old bookmark).
const ALWAYS_NAV = new Set(["/chat", "/notifications", "/settings", "/dashboard"]);

export function RequirePage({ page, children }) {
  const { can, perms, isAdmin } = usePermissions();
  if (!isAdmin && !perms) return null;
  if (!can(page, "view")) return <Navigate to="/dashboard" replace />;
  return children;
}

export const NAV = [
  { title: "OVERVIEW", items: [
    { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
    { name: "Chat", path: "/chat", icon: MessageCircle },
    { name: "Notifications", path: "/notifications", icon: Bell },
  ]},
  { title: "CRM", items: [
    { name: "Properties", path: "/properties", icon: Hotel },
    { name: "Organisations", path: "/organisations", icon: Building2 },
    { name: "Contacts", path: "/contacts", icon: Users },
    { name: "Opportunities", path: "/opportunities", icon: TrendingUp },
  ]},
  { title: "ACTIONS", items: [
    { name: "To Do", path: "/todo", icon: ListChecks },
    { name: "Activities", path: "/actions/my", icon: CheckSquare },
  ]},
  { title: "PROMOTIONS", items: [
    { name: "Direct Campaigns", path: "/campaigns/direct", icon: Mail },
    { name: "Agent Campaigns", path: "/campaigns/agent", icon: Megaphone },
    { name: "Collaborations", path: "/collaborations", icon: Handshake },
  ]},
  { title: "OPERATIONS", items: [
    { name: "Print Orders", path: "/print-orders", icon: Printer },
    { name: "Alcohol Orders", path: "/alcohol-orders", icon: Wine },
    { name: "Prize Vouchers", path: "/prize-vouchers", icon: Ticket },
    { name: "Logins", path: "/logins", icon: KeyRound },
    { name: "Rate Contracts", path: "/contracted-rates", icon: Percent },
    { name: "Rates Management", path: "/rates", icon: Gauge },
  ]},
  { title: "PERFORMANCE", items: [
    { name: "KPIs", path: "/kpis", icon: Target },
    { name: "Reports", path: "/reports", icon: BarChart3 },
    { name: "Stats", path: "/stats", icon: LineChart },
    { name: "Forecasts", path: "/forecasts", icon: TrendingUp },
  ]},
  { title: "ADMIN", items: [
    { name: "Noticeboard", path: "/noticeboard", icon: Pin },
    { name: "Event Calendar", path: "/event-calendar", icon: CalendarRange },
    { name: "Procedures", path: "/procedures", icon: BookOpen },
    { name: "Settings", path: "/settings", icon: Settings },
  ]},
];

function Sidebar({ mobileOpen, onClose }) {
  const { user, logout } = useAuth();
  const { can, hiddenNav } = usePermissions();
  const [layout, setLayout] = useState(() => user?.preferences?.nav_layout || {});
  const [editing, setEditing] = useState(false);
  const saveLayout = (next) => {
    setLayout(next);
    client.put("/me/preferences", { preferences: { nav_layout: next } }).catch(() => toast.error("Could not save menu"));
  };
  const allowed = applyNavLayout(NAV.map((g) => {
    const items = g.items.filter((it) => {
      if (ALWAYS_NAV.has(it.path)) return !hiddenNav.includes(it.path);
      const page = PATH_PAGE[it.path];
      if (!page && process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(`[AppShell] NAV item "${it.name}" (${it.path}) has no PATH_PAGE entry — it will never show for a non-admin role.`);
      }
      return !hiddenNav.includes(it.path) && can(page, "view");
    });
    return { ...g, items };
  }).filter((g) => g.items.length > 0), layout);
  const userHidden = layout.hidden || [];
  const nav = allowed.map((g) => ({ ...g, items: g.items.filter((it) => !userHidden.includes(it.path)) })).filter((g) => g.items.length > 0);
  return (
    <>
      {mobileOpen && <div className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden" onClick={onClose} />}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-slate-200 bg-slate-50 transition-transform lg:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-16 items-center gap-2.5 border-b border-slate-200 px-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-sky-700 shadow-sm">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-heading text-base font-extrabold leading-none tracking-tight text-slate-900">DreamCRM</p>
            <p className="text-[10px] font-medium text-slate-400">Dreamtime Resorts</p>
          </div>
          <button className="ml-auto lg:hidden" onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>

        <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-3.5">
          {editing && <NavEditor nav={allowed} layout={layout} onChange={saveLayout} />}
          {!editing && nav.map((group) => (
            <div key={group.title}>
              <p className="mb-1 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">{group.title}</p>
              <div className="space-y-px">
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    data-testid={`nav-${item.name.toLowerCase().replace(/ /g, "-")}`}
                    className={({ isActive }) =>
                      cn(
                        "group relative flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-medium transition-all",
                        isActive
                          ? "bg-sky-50 text-sky-700"
                          : "text-slate-600 hover:bg-white hover:text-slate-900"
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && <span className="absolute left-0 h-5 w-1 rounded-r-full bg-sky-600" />}
                        <item.icon className={cn("h-[18px] w-[18px]", isActive ? "text-sky-600" : "text-slate-400 group-hover:text-slate-600")} />
                        {item.name}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2 px-1 pt-1">
            <button type="button" onClick={() => setEditing((x) => !x)} className={cn("flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold", editing ? "bg-sky-600 text-white hover:bg-sky-700" : "text-slate-400 hover:bg-white hover:text-slate-700")} data-testid="nav-edit-toggle">
              {editing ? <><Check className="h-3.5 w-3.5" />Done</> : <><Pencil className="h-3 w-3" />Customise menu</>}
            </button>
            {editing && <button type="button" onClick={() => saveLayout({})} className="text-[11px] font-semibold text-slate-400 hover:text-slate-700" data-testid="nav-reset">Reset</button>}
          </div>
        </nav>

        <div className="border-t border-slate-200 p-3">
          <div className="flex items-center gap-2.5 rounded-lg px-2 py-2" data-testid="sidebar-user">
            <Avatar name={user?.name} color={user?.color} className="h-9 w-9 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-800">{user?.name}</p>
              <p className="truncate text-[11px] capitalize text-slate-400">{user?.role}</p>
            </div>
            <button
              data-testid="logout-button"
              title="Sign out"
              onClick={() => { logout(); toast.success("Signed out"); }}
              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-white hover:text-slate-700"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

function DailyGreeting() {
  const [greeting, setGreeting] = useState("");
  useEffect(() => {
    client.get("/me/daily-greeting").then(({ data }) => setGreeting(data.greeting || "")).catch(() => {});
  }, []);
  if (!greeting) return <div className="flex-1" />;
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2" data-testid="daily-greeting">
      <Sparkles className="h-4 w-4 shrink-0 text-amber-400" />
      <p className="truncate text-sm font-medium text-slate-600">{greeting}</p>
    </div>
  );
}

function Header({ onMenu }) {
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/85 px-4 backdrop-blur-md lg:px-8">
      <button className="lg:hidden" onClick={onMenu} data-testid="mobile-menu-button"><Menu className="h-5 w-5 text-slate-500" /></button>
      <div className="flex min-w-0 flex-1"><DailyGreeting /></div>
      <ChatButton />
      <NotificationBell />
      <TabAlerts />
      <ChangeWatcher />
    </header>
  );
}

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Warm picker caches in the background so link fields open instantly
  useEffect(() => { const t = setTimeout(() => { warmOptions(); fetchPropertyGroups(); }, 1500); return () => clearTimeout(t); }, []);
  return (
    <div className="min-h-screen bg-white">
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <div className="lg:pl-64">
        <SystemStatus />
        <Header onMenu={() => setMobileOpen(true)} />
        <main className="px-4 py-6 pb-24 lg:px-8 lg:py-8">
          <InstallBanner />
          <PasskeyPrompt />
          <Outlet />
        </main>
      </div>
      <QuickAddFab />
    </div>
  );
}
